<template>
  <menu-filters
    :menu-data="menuData"
    :is-open="isFilterBarOpen"
    :calories-min="params.filterCaloriesMin"
    :calories-max="params.filterCaloriesMax"
    @on-close="onToggleFilterBar(false)"
    @on-filters-change="onFiltersChange"
  />
  <div ref="top"></div>
  <span v-if="showContentLoaded" class="sr-only" role="alert" aria-live="assertive">
    {{ t('iorder-content-loaded') }}
  </span>
  <div ref="navigation" :class="{ 'iorder-menu__sticky-nav': !$isPageEditing }">
    <menu-navigation
      v-if="menuData.activeMenu"
      :menu-data="menuData"
      :seasonal-menus="fields.seasonalMenus"
      :is-filter-bar-open="isFilterBarOpen"
      @menu-item-click="onChangeActiveMenu"
      @open-filter-bar="onToggleFilterBar(true)"
    />
  </div>
  <menu-info-bar
    v-if="!isLoading"
    :active-menu-item="menuData.activeMenuItem"
    :menus-details="fields.menusDetails"
    :allergen-data="allergenData"
    :current-day-serving-hours="fields.currentDayServingHours"
    :current-day-opening-hours="fields.currentDayOpeningHours"
    :download-menu-cta="fields.downloadMenuCta"
  />
  <popular-dishes :cards="pdCards" />
  <menu-list
    :menu-data="menuData"
    :nav-ref="navigation"
    :separators-type="fields.sectionSeparatorsType"
    :deals="fields.dealCards"
    :layout="params.layout"
    @change-active-item="onChangeActiveSubmenu"
    @scrolled-to-active-item="onScrolledToActiveItem"
  />
  <p v-if="isError || isNoData" class="iorder-menu__error" role="alert" aria-live="assertive">
    {{ isError ? t('menus-error-message') : t('menus-no-results-message') }}
  </p>
  <deal-carousel
    :title="fields.carouselTitle?.value"
    :description="fields.carouselDescription?.value"
    :cards="fields.dealCards"
  />
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  onServerPrefetch,
  nextTick,
  reactive,
  provide,
  watch,
  useTemplateRef,
} from 'vue';
import { isServer } from '@sitecore-jss/sitecore-jss/utils';
import { useHead } from '@unhead/vue';
import { replaceTokens } from '../../utils/replaceTokens';
import MenuNavigation from './MenuNavigation/MenuNavigation.vue';
import MenuList from './MenuList/MenuList.vue';
import MenuInfoBar from './MenuInfoBar/MenuInfoBar.vue';
import MenuFilters from './MenuFilters/MenuFilters.vue';
import PopularDishes from './PopularDishes/PopularDishes.vue';
import { fetchMenus } from '../../utils/queries/useMenusQuery';
import { getCurrentQueryObject } from '../../utils/getCurrentQueryObject';
import { slugifyFilterValue, deslugifyValue } from '../../utils/urlFilters';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useThrottleFn, useScroll } from '@vueuse/core';
import { useAppStore } from '../../stores/appStore';
import filterIOrderMenus from './filterIOrderMenus';
import { useMenuInteractionTracking } from '../../data-layer/hooks/useMenuInteractionTracking';
import DealCarousel from './DealCarousel/DealCarousel.vue';
import { useJssStore } from '../../stores/jssStore';
import type { MenuItem } from '@/types/menu';
import type { MenuData, MenuFilter, IOrderMenuMainProps } from './types';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const appStore = useAppStore();
const { isScrolling } = useScroll(isServer() ? undefined : window);

const props = withDefaults(defineProps<IOrderMenuMainProps>(), {
  fields: () => ({
    dealCards: { value: [] },
  }),
  params: () => ({}),
});

const jssStore = useJssStore();
const useSSR =
  (jssStore.$state as unknown as Record<string, unknown>)['Feature.Menus.UseSSR'] === 'true';
const { trackMenuInteraction } = useMenuInteractionTracking();

const isLoading = defineModel<boolean>('isLoading');

const top = useTemplateRef('top');
const navigation = useTemplateRef('navigation');
const isMounted = ref(false);
let pendingScroll: (() => void) | null = null;
const isAutoScrolling = ref(false);
let autoScrollTimeout: ReturnType<typeof setTimeout> | null = null;
const showContentLoaded = ref(false);
const isError = ref(false);
const isNoData = ref(false);
const isFilterBarOpen = ref(false);
const dl_loggedSubmenu = ref<number | null>(null);

const menuData = reactive<MenuData>({
  menus: [],
  filters: [],
  activeMenu: null,
  activeMenuItem: null,
  activeSubmenu: null,
  activeFilters: [],
});

const fields = props.fields;

const allergenData = computed(() => ({
  allergensLink: fields.allergensLink?.value,
}));
provide('allergenData', allergenData);
provide('bookingUrl', fields.bookingUrl?.value);

const pdCards = computed(() => {
  const activeName = menuData.activeMenuItem?.name.toLowerCase();
  return fields.menusDetails?.value?.find((menu) => menu.key?.toLowerCase() === activeName) || {};
});

const resolveMetaTemplate = (template: string | undefined) => {
  if (!template || !menuData.activeMenuItem) return '';
  return replaceTokens(template, { menuName: menuData.activeMenuItem.name }, 'Menu');
};

const parsedMetaTitle = computed(() => resolveMetaTemplate(fields?.metaTitleTemplate?.value));

const parsedMetaDescription = computed(() =>
  resolveMetaTemplate(fields?.metaDescriptionTemplate?.value)
);

const headConfig = computed(() => {
  const config: { title?: string; meta?: { name: string; content: string }[] } = {};

  if (parsedMetaTitle.value) {
    config.title = parsedMetaTitle.value;
  }

  if (parsedMetaDescription.value) {
    config.meta = [{ name: 'description', content: parsedMetaDescription.value }];
  }

  return config;
});

useHead(headConfig);

const onFiltersChange = (filters: MenuFilter[]) => {
  menuData.activeFilters = filters;
  const activeMenuItem = menuData.menus.find((menu) => menu.id === menuData.activeMenu) || null;
  menuData.activeMenuItem = filterIOrderMenus(activeMenuItem, menuData.activeFilters);
};

const onToggleFilterBar = (isOpen: boolean) => {
  isFilterBarOpen.value = isOpen;
};

const onChangeActiveMenu = (menuId: number) => {
  if (menuId === menuData.activeMenu) return;
  menuData.activeMenu = menuId;
  scrollToTop();
  const activeMenuItem = menuData.menus.find((menu) => menu.id === menuId) || null;
  if (!activeMenuItem) return;
  menuData.activeMenuItem = filterIOrderMenus(activeMenuItem, menuData.activeFilters);
  const activeSubmenu = activeMenuItem.menuTree?.[0];
  menuData.activeSubmenu = activeSubmenu?.id;
  registerMenuViewInDataLayer(activeMenuItem.name, activeSubmenu?.name);
  const currentQuery = getCurrentQueryObject();
  const slugifiedName = slugifyFilterValue(activeMenuItem.name);
  if (slugifiedName === currentQuery['type']) return;
  router.push({
    query: {
      type: slugifiedName,
    },
  });
};

const onChangeActiveSubmenu = (submenuId: number) => {
  if (isAutoScrolling.value) return;
  menuData.activeSubmenu = submenuId;
  setSectionQuery(submenuId);
};

const onScrolledToActiveItem = useThrottleFn((submenuId: number) => {
  if (isAutoScrolling.value) return;
  setSectionQuery(submenuId);
}, 100);

const scrollToMenu = (menuId: number | null) => {
  menuData.activeSubmenu = menuId;
  setSectionQuery(menuId);

  const el = document.querySelector(`[id="${menuId}"]`);
  scrollToElement(el);
};

const scrollToTop = () => {
  const topPos = (top.value?.getBoundingClientRect().top ?? 0) - appStore.bodyOffset;
  if (topPos < 0) nextTick(() => scrollToElement(top.value));
};

const endAutoScroll = () => {
  isAutoScrolling.value = false;
  if (autoScrollTimeout) {
    clearTimeout(autoScrollTimeout);
    autoScrollTimeout = null;
  }
  window.removeEventListener('scrollend', endAutoScroll);
};

const beginAutoScroll = () => {
  isAutoScrolling.value = true;
  window.addEventListener('scrollend', endAutoScroll, { once: true });
  // Fallback for browsers without 'scrollend' support (e.g. older Safari).
  if (autoScrollTimeout) clearTimeout(autoScrollTimeout);
  autoScrollTimeout = setTimeout(endAutoScroll, 1000);
};

const scrollToElement = (elRef: Element | null) => {
  if (!elRef) return;
  const elementBelowStickyNav =
    (top.value?.getBoundingClientRect().top ?? 0) < elRef.getBoundingClientRect().top;

  const offset =
    appStore.bodyOffset +
    (elementBelowStickyNav ? (navigation.value as HTMLDivElement).offsetHeight : 0);
  const elPos = window.scrollY + elRef.getBoundingClientRect().top - offset + 1;

  setTimeout(() => {
    beginAutoScroll();
    window.scrollTo({
      top: elPos,
      left: 0,
      behavior: 'smooth',
    });
  }, 0);
};

const setSectionQuery = (menuId: number | null) => {
  const activeCategoryName = menuData.activeMenuItem?.menuTree?.find(
    (category) => category.id === menuId
  )?.name;

  if (!activeCategoryName) return;

  const currentQuery = getCurrentQueryObject();
  const slugifiedSection = slugifyFilterValue(activeCategoryName);
  if (slugifiedSection === currentQuery['section']) return;

  const updatedQuery = {
    ...currentQuery,
    type: slugifyFilterValue(menuData.activeMenuItem?.name),
    section: slugifiedSection,
  };

  if (globalThis?.IS_STORYBOOK) return;
  const path = router.resolve({ query: updatedQuery })?.fullPath.toLowerCase();
  path && history.replaceState({ ...history.state, current: path }, '', path);
};

const registerMenuViewInDataLayer = (menuSection: string, menuSubsection?: string) => {
  trackMenuInteraction(null, menuSection, 'view', {
    menu_subsection: menuSubsection?.toLowerCase() || '',
  });
  dl_loggedSubmenu.value = menuData.activeSubmenu;
};

const onLoaded = () => {
  isLoading.value = false;

  if (isServer()) return;

  showContentLoaded.value = true;

  setTimeout(() => {
    showContentLoaded.value = false;
  }, 5000);
};

const pickAndScrollToActiveCategory = (
  activeMenuFromQuery: MenuItem,
  type: string,
  section: string
) => {
  const sectionNames = activeMenuFromQuery.menuTree?.map((c) => c.name) || [];
  const deslugifiedSection = deslugifyValue(section, sectionNames);
  const activeCategoryFromQuery = activeMenuFromQuery.menuTree?.find(
    (category) => category.name === deslugifiedSection
  );
  menuData.activeSubmenu = activeCategoryFromQuery?.id ?? activeMenuFromQuery.menuTree?.[0]?.id;

  if (isServer()) return;

  const performScroll = () => {
    nextTick(() => {
      const menuNames = menuData.menus.map((m) => m.name);
      const deslugifiedType = deslugifyValue(type, menuNames);
      if (!type || deslugifiedType !== activeMenuFromQuery.name) return;

      if (section) scrollToMenu(menuData.activeSubmenu);
      else scrollToElement(document.querySelector(`.content-navigation`));
    });
  };

  // Defer the initial scroll until after mount so it runs on a settled layout.
  // Running it during setup-time hydration (SSR cache path) causes the page to jump.
  if (isMounted.value) performScroll();
  else pendingScroll = performScroll;
};

const onInit = async (data: { menus: MenuItem[] }) => {
  menuData.menus = data.menus || [];
  const { type, section } = router.currentRoute.value.query;
  const menuNames = menuData.menus.map((m) => m.name);
  const deslugifiedType = deslugifyValue(type as string, menuNames);
  const activeMenuFromQuery =
    menuData.menus.find((menu) => menu.name === deslugifiedType) ?? menuData.menus[0];
  if (activeMenuFromQuery) {
    menuData.activeMenu = activeMenuFromQuery.id;
    menuData.activeMenuItem = activeMenuFromQuery;
    pickAndScrollToActiveCategory(activeMenuFromQuery, type as string, section as string);
    onLoaded();
  } else {
    isNoData.value = true;
    isLoading.value = false;
  }
};

const loadMenus = async () => {
  if (!(jssStore.venueId && fields.apiUrl?.value && fields.subscriptionKey?.value)) {
    isError.value = true;
    isLoading.value = false;
    return;
  }

  if (useSSR) {
    const cachedMenus = appStore.getCachedMenus(jssStore.venueId);
    if (cachedMenus) {
      await onInit(cachedMenus);
      return;
    }
  }

  try {
    const response = await fetchMenus(
      jssStore.venueId,
      fields.apiUrl.value,
      fields.subscriptionKey.value
    );
    const data = response.data || { menus: [] };
    if (useSSR) appStore.setCachedMenus(jssStore.venueId, data);
    await onInit(data);
  } catch {
    isError.value = true;
    isLoading.value = false;
  }
};

if (useSSR) {
  const initialCachedMenus = appStore.getCachedMenus(jssStore.venueId);
  if (initialCachedMenus) {
    void onInit(initialCachedMenus);
  }
}

onServerPrefetch(async () => {
  if (!useSSR) return;
  await loadMenus();
});

onMounted(() => {
  isMounted.value = true;
  if (pendingScroll) {
    pendingScroll();
    pendingScroll = null;
  }
  if (menuData.menus.length || isError.value || isNoData.value) return;
  void loadMenus();
});

onBeforeUnmount(() => {
  endAutoScroll();
});

watch([() => menuData.activeSubmenu, isScrolling], async ([newSubmenu]) => {
  await new Promise((resolve) => setTimeout(resolve, 50));

  if (isScrolling.value || dl_loggedSubmenu.value === newSubmenu) return;

  const activeSubmenu = menuData.activeMenuItem?.menuTree?.find(
    (submenu) => submenu.id === newSubmenu
  );

  registerMenuViewInDataLayer(menuData.activeMenuItem?.name, activeSubmenu?.name);
});

watch(
  () => route.query,
  (query) => {
    if (!menuData.menus.length) return;

    const type = query.type as string | undefined;
    const section = query.section as string | undefined;

    if (type) {
      const menuNames = menuData.menus.map((m) => m.name);
      const deslugifiedType = deslugifyValue(type, menuNames);
      const menuFromQuery = menuData.menus.find((m) => m.name === deslugifiedType);
      if (menuFromQuery && menuFromQuery.id !== menuData.activeMenu) {
        menuData.activeMenu = menuFromQuery.id;
        menuData.activeMenuItem = filterIOrderMenus(menuFromQuery, menuData.activeFilters);
      }
    }

    if (section && menuData.activeMenuItem?.menuTree) {
      const sectionNames = menuData.activeMenuItem.menuTree.map((s) => s.name);
      const deslugifiedSection = deslugifyValue(section, sectionNames);
      const sectionFromQuery = menuData.activeMenuItem.menuTree.find(
        (s) => s.name === deslugifiedSection
      );
      if (sectionFromQuery && sectionFromQuery.id !== menuData.activeSubmenu) {
        menuData.activeSubmenu = sectionFromQuery.id;
        nextTick(() => scrollToMenu(sectionFromQuery.id));
      }
    }
  }
);
</script>

<style lang="scss">
@import './scss/IOrderMenu.scss';
</style>
